import { useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import ImageUploader, { ImageUploaderHandle } from "@/components/ImageUploader";
import NavBar from "@/components/NavBar";
import SEOContent from "@/components/SEOContent";
import SEOHead from "@/components/SEOHead";
import BreadcrumbSEO from "@/components/BreadcrumbSEO";
import CompressionExample from "@/components/CompressionExample";
import ExampleImages from "@/components/ExampleImages";
import { getToolBySlug } from "@/data/tools";
import { useAnalytics } from "@/hooks/useAnalytics";
import { useIsMobile } from "@/hooks/use-mobile";
import { lazy, Suspense } from "react";

const AdSlot = lazy(() => import("@/components/AdSlot"));

const featuredSlugs = ['comprimir-jpg', 'comprimir-png', 'comprimir-webp', 'convertir-jpg-webp', 'reducir-imagen-200kb', 'optimizar-imagen-online'];
const featuredTools = featuredSlugs.map(s => getToolBySlug(s)!).filter(Boolean);

const Index = () => {
  const uploaderRef = useRef<ImageUploaderHandle>(null);
  const { trackEvent } = useAnalytics();
  const isMobile = useIsMobile();

  const scrollTracked = useRef({ s50: false, s75: false, s100: false });

  useEffect(() => {
    trackEvent('page_view', { page: '/', page_type: 'home', device_type: isMobile ? 'mobile' : 'desktop' });
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollHeight <= 0) return;
      const pct = (window.scrollY / scrollHeight) * 100;
      if (pct >= 50 && !scrollTracked.current.s50) {
        scrollTracked.current.s50 = true;
        trackEvent('scroll_50', { page: '/' });
      }
      if (pct >= 75 && !scrollTracked.current.s75) {
        scrollTracked.current.s75 = true;
        trackEvent('scroll_75', { page: '/' });
      }
      if (pct >= 100 && !scrollTracked.current.s100) {
        scrollTracked.current.s100 = true;
        trackEvent('scroll_100', { page: '/' });
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [trackEvent]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const section = params.get('section');
    if (section) {
      setTimeout(() => {
        const el = document.getElementById(section);
        if (el) {
          const navbarHeight = 64;
          window.scrollTo({ top: el.offsetTop - navbarHeight - 20, behavior: 'smooth' });
        }
        window.history.replaceState({}, '', '/');
      }, 300);
    }
  }, []);

  const handleExampleSelect = (file: File) => {
    uploaderRef.current?.addAndCompressFiles([file]);
  };

  const steps = [
    {
      number: "1",
      title: "Sube tus imágenes",
      description: "Arrastra y suelta tus imágenes JPEG o PNG, o haz clic para seleccionarlas"
    },
    {
      number: "2", 
      title: "Compresión automática",
      description: "La compresión comienza inmediatamente después de subir tus imágenes"
    },
    {
      number: "3",
      title: "Descarga",
      description: "Descarga tus imágenes optimizadas individual o grupalmente"
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEOHead 
        title="Comprimir Imágenes Online Gratis (JPG, PNG, WebP) | CompresorJPEG"
        description="Comprime y optimiza imágenes JPG, PNG, WebP gratis. Reduce hasta 90% del tamaño sin perder calidad. Herramienta rápida, segura y sin instalación."
        keywords="comprimir imagenes, compresor jpg, comprimir png, optimizar webp, reducir tamaño imagen, comprimir fotos online, optimizar imagenes web"
        canonical="https://compresorjpeg.com/"
      />
      
      <NavBar />
      
      <div className="container mx-auto px-4">
        <BreadcrumbSEO />
      </div>
      
      <header className="text-center pt-4 pb-2 animate-fade-in">
        <div className="container mx-auto px-4">
          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
            Comprimir imágenes gratis
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-1">
            Reduce el tamaño de tus imágenes hasta <strong>90%</strong> sin perder calidad.
          </p>
          <p className="text-sm text-muted-foreground">
            JPG • PNG • WebP
          </p>
        </div>
      </header>

      <main className="container mx-auto px-4 pb-8">
        <section className="animate-fade-in mb-8" aria-label="Herramienta de compresión de imágenes">
          <h2 className="sr-only">Compresor de Imágenes Online</h2>
          <ImageUploader ref={uploaderRef} />
        </section>

        <section className="animate-fade-in mb-12" aria-label="Imágenes de ejemplo">
          <ExampleImages onSelectExample={handleExampleSelect} />
        </section>

        <section className="animate-fade-in mb-16" aria-label="Ejemplo de compresión">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground text-center mb-6">
            Mira cuánto puedes reducir el tamaño de tus imágenes
          </h2>
          <CompressionExample />
        </section>

        <section id="how-it-works-section" className="text-center mb-16 animate-fade-in">
          <h2 className="text-3xl font-bold text-foreground mb-6">
            ¿Cómo comprimir imágenes online?
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto mb-12 text-lg">
            Sigue estos 3 sencillos pasos para <strong>comprimir tus imágenes</strong> y <strong>optimizar el tamaño de archivo</strong> sin perder calidad visual:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {steps.map((step, index) => (
              <article 
                key={index} 
                className="text-center group transform transition-all duration-500 hover:scale-105"
              >
                <div className="w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold transition-all duration-300 group-hover:shadow-xl group-hover:scale-110">
                  {step.number}
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-3 transition-colors duration-300 group-hover:text-primary">
                  {step.title}
                </h3>
                <p className="text-muted-foreground">
                  {step.description}
                </p>
              </article>
            ))}
          </div>
        </section>

        <Suspense fallback={null}>
          <AdSlot position="inline" className="max-w-3xl mx-auto mb-12" />
        </Suspense>

        <section className="animate-fade-in mb-16" aria-label="Herramientas populares">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
              Herramientas populares
            </h2>
            <p className="text-muted-foreground">
              Comprime, convierte y optimiza tus imágenes con nuestras herramientas especializadas
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
            {featuredTools.map(tool => (
              <Link
                key={tool.slug}
                to={`/herramientas/${tool.slug}`}
                className="group p-5 rounded-xl border border-border bg-card hover:shadow-md hover:border-primary/30 transition-all duration-200"
              >
                <h3 className="text-base font-semibold text-foreground group-hover:text-primary transition-colors mb-1">
                  {tool.title}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {tool.subtitle}
                </p>
              </Link>
            ))}
          </div>
          <div className="text-center mt-6">
            <Link
              to="/herramientas"
              className="inline-flex items-center text-sm font-medium text-primary hover:underline"
            >
              Ver todas las herramientas →
            </Link>
          </div>
        </section>

        <section id="ventajas-section" className="animate-fade-in mb-16">
          <SEOContent showOnlyFirstSections={true} />
        </section>

        <section id="caracteristicas-section" className="mb-16 animate-fade-in">
          <SEOContent showOnlyFirstSections={false} />
        </section>
      </main>

      <footer className="bg-muted border-t border-border py-8 mt-16">
        <div className="container mx-auto px-4 text-center">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-8 mb-8">
            <div>
              <h3 className="font-semibold text-foreground mb-3">Formatos Soportados</h3>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>Comprimir JPEG/JPG</li>
                <li>Optimizar PNG</li>
                <li>Convertir WebP</li>
                <li>Reducir GIF</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-foreground mb-3">Casos de Uso</h3>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>Optimización para web</li>
                <li>Redes sociales</li>
                <li>Email marketing</li>
                <li>Almacenamiento eficiente</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-foreground mb-3">Ventajas SEO</h3>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>Velocidad de carga mejorada</li>
                <li>Mejor Core Web Vitals</li>
                <li>Reducción de ancho de banda</li>
                <li>Experiencia de usuario superior</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-foreground mb-3">Recursos</h3>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>
                  <Link to="/faq" className="hover:text-primary transition-colors duration-200">
                    Preguntas Frecuentes
                  </Link>
                </li>
                <li>
                  <Link to="/quitar-fondo" className="hover:text-primary transition-colors duration-200">
                    Quitar Fondo de Imagen
                  </Link>
                </li>
                <li>
                  <Link to="/blog" className="hover:text-primary transition-colors duration-200">
                    Blog y Guías
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-foreground mb-3">Legal</h3>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>
                  <Link to="/privacidad" className="hover:text-primary transition-colors duration-200">
                    Política de Privacidad
                  </Link>
                </li>
                <li>
                  <Link to="/terminos" className="hover:text-primary transition-colors duration-200">
                    Términos de Uso
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          <div className="text-xs text-muted-foreground border-t border-border pt-4">
            <p>© 2025 CompresorJPEG. Herramienta gratuita para comprimir imágenes online. Todos los derechos reservados.</p>
            <p className="mt-2">Comprime JPG, PNG, WebP y más formatos sin perder calidad. 100% gratuito y seguro.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;
